import Button from '@/components/content/Button'
import Text from '@/components/content/Text'
import useClickOutside from '@/hooks/useClickOutside'
import { DeliveryAreaItem, UpdateDeliveryAreaReq } from '@/services/deliveryAreas/types'
import { colors, colorSystem } from '@/styles/theme'
import { HStack, Modal, VStack } from '@musinsa/mwms-ds'
import { RefObject, useRef, useState } from 'react'
import styled from 'styled-components'
import { AgGridReact } from 'ag-grid-react'
import useModal from '@/hooks/useModal'
import { BulkUpdateModal } from './BulkUpdateModal'
import MRender from '@/utils/modal/MRender'
import { useCheckedNum } from '@/components/table/GridTable.atoms'
import { tokens } from '@musinsa/layer-tokens/desktop'

type Props = {
  $grid: RefObject<AgGridReact<DeliveryAreaItem>>
  onDelete: () => void
  onRefetch: () => void
  onSave: () => void
}

export type BulkKey = keyof Pick<
  UpdateDeliveryAreaReq,
  'deliveryLeadDay' | 'remoteArea' | 'isHolidayDeliveryAvailable' | 'defaultCarrier' | 'holidayCarrier'
>

const options: { label: string; value: BulkKey }[] = [
  { label: '리드타임 변경', value: 'deliveryLeadDay' },
  { label: '휴일배송여부 변경', value: 'isHolidayDeliveryAvailable' },
  { label: '도서산간여부 변경', value: 'remoteArea' },
  { label: '주중 운송사 변경', value: 'defaultCarrier' },
  { label: '휴일 운송사 변경', value: 'holidayCarrier' },
]

export function DeliveryAreasTableTitle({ $grid, onDelete, onSave, onRefetch }: Props) {
  const optionList = useModal()

  const bulkOptionsRef = useRef<HTMLDivElement>(null)

  const [bulkKey, setBulkKey] = useState<BulkKey | null>(null)
  const isBulkChangeModalOpen = bulkKey !== null

  const [checkedNum] = useCheckedNum()

  const isButtonDisabled = checkedNum === 0

  useClickOutside(bulkOptionsRef, optionList.onCloseModal)

  const handleDeleteClick = () => {
    MRender.show(<DeleteModal checkedNum={checkedNum} onClose={MRender.destory} onSubmit={onDelete} />)
  }

  return (
    <>
      <HStack gap={16}>
        <HStack gap={2} style={{ flex: '0 0 auto' }}>
          <Text kind="Body_14_bold" color={checkedNum ? tokens.layerDesktop['fg-accent'] : colorSystem.text.common.tertiary}>
            {checkedNum.toLocaleString()}
          </Text>
          <Text kind="Body_14_regular">건 선택</Text>
        </HStack>
        <HStack gap={8} align="center">
          <Button fit disabled={isButtonDisabled} size="sm" kind="secondary" type="button" onClick={handleDeleteClick}>
            삭제
          </Button>
          <Button fit disabled={isButtonDisabled} size="sm" kind="secondary" type="button" onClick={onSave}>
            저장
          </Button>
          <Divider />
          <div ref={bulkOptionsRef} style={{ position: 'relative' }}>
            <Button fit disabled={isButtonDisabled} size="sm" kind="secondary" type="button" onClick={optionList.onToggleModal}>
              일괄변경
            </Button>
            {optionList.open && (
              <OptionList>
                {options.map((option) => (
                  <OptionItem
                    key={option.value}
                    onClick={() => {
                      setBulkKey(option.value)
                      optionList.onCloseModal()
                    }}
                  >
                    <Text kind="Body_14_regular" color={colorSystem.text.common.primary}>
                      {option.label}
                    </Text>
                  </OptionItem>
                ))}
              </OptionList>
            )}
          </div>
        </HStack>
      </HStack>

      {isBulkChangeModalOpen && <BulkUpdateModal $grid={$grid} onClose={() => setBulkKey(null)} bulkKey={bulkKey} onRefetch={onRefetch} />}
    </>
  )
}

function DeleteModal({ checkedNum, onClose, onSubmit }: { checkedNum: number; onClose: () => void; onSubmit: () => void }) {
  return (
    <Modal open onClose={onClose} size="sm">
      <VStack gap={24} align="center">
        <VStack gap={8} align="center">
          <Text kind="Headline_20_bold">{checkedNum.toLocaleString()}건의 우편번호를 삭제하시겠어요?</Text>
          <Text kind="Body_14_regular" color={colorSystem.text.common.tertiary}>
            삭제된 항목은 복구할 수 없어요.
          </Text>
        </VStack>
        <HStack gap={8}>
          <Button fit kind="secondary" type="button" onClick={onClose}>
            취소
          </Button>
          <Button
            fit
            kind="primary_light"
            type="button"
            onClick={() => {
              onSubmit()
              onClose()
            }}
          >
            삭제
          </Button>
        </HStack>
      </VStack>
    </Modal>
  )
}

const Divider = styled.div`
  width: 1px;
  height: 16px;
  background-color: ${colors.gray300};
`

const OptionList = styled.ul`
  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  z-index: 1000;

  display: flex;
  flex-direction: column;
  gap: 8px;

  width: 160px;
  padding: 8px;
  border-radius: 6px;
  border: 1px solid ${colorSystem.border.element};
  box-shadow: 0px 10px 15px 0px rgba(0, 0, 0, 0.05);
  background: ${colors.white};

  animation: fadeIn 0.2s ease-in-out;
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`

const OptionItem = styled.li`
  /* height: 28px; */
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background-color: ${colors.gray100};
  }
`
